import { getSafeHeader } from './headers';

export interface SimpleStaticModel {
  name: string;
  description: string;
  maxTokens: number;
  maxOutputTokens: number;
  toolCall: boolean;
  reasoning: boolean;
  attachment: boolean;
  cost?: {
    input: number;
    output: number;
    cache?: { read: number; write: number };
  };
}

export const STATIC_MODELS_SIMPLE: Record<string, SimpleStaticModel> = {
  'gemini-3.8-flash': {
    name: 'Gemini 3.8 Flash',
    description: 'Gemini 3.8 Flash base model. Select tier at runtime.',
    maxTokens: 1048576,
    maxOutputTokens: 65536,
    toolCall: true,
    reasoning: true,
    attachment: true
  },
  'gemini-4-argon': {
    name: 'Gemini 4 Argon',
    description: 'Gemini 4 Argon base model. Select tier at runtime.',
    maxTokens: 1048576,
    maxOutputTokens: 65536,
    toolCall: true,
    reasoning: true,
    attachment: true
  },
  'claude-opus-5-5': {
    name: 'Claude Opus 5.5',
    description: 'Claude Opus 5.5 deep reasoning model. Select tier at runtime.',
    maxTokens: 1000000,
    maxOutputTokens: 128000,
    toolCall: true,
    reasoning: true,
    attachment: true
  },
  'claude-sonnet-5-5': {
    name: 'Claude Sonnet 5.5',
    description: 'Claude Sonnet 5.5 deep reasoning model. Select tier at runtime.',
    maxTokens: 1000000,
    maxOutputTokens: 128000,
    toolCall: true,
    reasoning: true,
    attachment: true
  },
  'gpt-oss-120b-medium': {
    name: 'GPT-OSS 120B (Medium)',
    description: 'GPT open-source 120B parameter medium tier model, excellent performance in local deployment or specific open-source benchmarks.',
    maxTokens: 131072,
    maxOutputTokens: 32768,
    toolCall: true,
    reasoning: true,
    attachment: false
  }
};

export const TIER_MAPPING: Record<string, { low: string; high: string; medium?: string; minimal?: string } & Record<string, string | undefined>> = {
  'gemini-3.8-flash': {
    low: 'gemini-3.8-flash-low',
    medium: 'gemini-3.8-flash-medium',
    high: 'gemini-3.8-flash-high'
  },
  'gemini-4-argon': {
    low: 'gemini-4-argon-low',
    medium: 'gemini-4-argon-medium',
    high: 'gemini-4-argon-high'
  },
  'claude-opus-5-5': {
    low: 'claude-opus-5-5-low',
    medium: 'claude-opus-5-5-medium',
    high: 'claude-opus-5-5-high'
  },
  'claude-sonnet-5-5': {
    low: 'claude-sonnet-5-5-low',
    medium: 'claude-sonnet-5-5-medium',
    high: 'claude-sonnet-5-5-high'
  }
};

/**
 * Resolves a model ID and optional request headers or suffix into the appropriate tier variant.
 */
export function resolveModelTier(
  baseModelId: string,
  headersOrInit?: RequestInit | unknown
): string {
  const parts = baseModelId.split('@');
  const base = parts[0] || '';
  const suffixTier = parts[1]?.toLowerCase();

  const mapping = TIER_MAPPING[base];
  if (!mapping) {
    return baseModelId;
  }

  const rawHeaders =
    headersOrInit && typeof headersOrInit === 'object' && 'headers' in headersOrInit
      ? (headersOrInit as RequestInit).headers
      : headersOrInit;

  const headerTier = getSafeHeader(rawHeaders, 'x-agy-tier')?.toLowerCase() || null;
  const requestedTier = headerTier || suffixTier;

  if (requestedTier && Object.prototype.hasOwnProperty.call(mapping, requestedTier)) {
    return mapping[requestedTier] || baseModelId;
  }

  return mapping['medium'] ?? mapping['high'] ?? baseModelId;
}
